import { useState, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { CloseIcon } from '@/icons'
import { setGlobalToast, useToast, ToastContext } from './toastUtils'

type ToastType = 'success' | 'error'

interface Toast {
  id: number
  message: string
  type: ToastType
}

let toastId = 0

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const showToast = useCallback((message: string, type: ToastType = 'error') => {
    const id = ++toastId
    setToasts((prev) => [...prev, { id, message, type }])
  }, [])

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {createPortal(
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
          {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} onDismiss={removeToast} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), 5000)
    return () => clearTimeout(timer)
  }, [toast.id, onDismiss])

  const bgColor = toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      data-testid={`toast-${toast.type}`}
      className={`${bgColor} text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 min-w-[300px] max-w-md animate-slide-in-right`}
    >
      <span className="flex-1">{toast.message}</span>
      <button
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
        className="text-white/80 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded"
      >
        <CloseIcon className="w-5 h-5" />
      </button>
    </div>
  )
}

// Component that connects toast context to global reference
export function ToastConnector() {
  const { showToast } = useToast()

  useEffect(() => {
    setGlobalToast(showToast)
    return () => {
      setGlobalToast(null)
    }
  }, [showToast])

  return null
}
